/**
 * Lançar / editar pela web com os MESMOS repositórios do app. Os tipos do celular: despesa,
 * receita, despesa no cartão e transferência; repetição em parcelas (createInstallment no
 * cartão, série manual em conta — igual ao form do app) ou recorrência (regra + 1ª ocorrência);
 * contas, cartões e categorias com os ícones de lá. Editar continua sendo "só esta ocorrência":
 * a série inteira, pelo app.
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createTransaction, createTransfer, deleteTransaction, getTransactionById, updateTransaction } from '@/db/repositories/transactionRepository';
import { createInstallment } from '@/db/repositories/installmentRepository';
import { createWithInitialOccurrence } from '@/db/repositories/recurringRepository';
import { brl } from '../viewer/lib/format';
import { installmentPreview, parseCurrentInstallment, planInstallments } from './installmentPlan';
import { generateId } from '@/lib/id';
import type { Model } from '../viewer/lib/model';
import type { TransactionRow } from '../viewer/lib/types';
import { ACCOUNT_TYPE_LABEL } from '../viewer/lib/institutions';
import { InstitutionLogo } from '../viewer/components/InstitutionLogo';
import { CategoryIcon } from '../viewer/components/CategoryIcon';
import { Picker, type PickerOption } from './Picker';
import { Actions, Field, FieldBox, Modal, field, parseAmount, today } from './ui';
import { askConfirm } from './confirm';
import { applySeriesDelete, applySeriesUpdate, chooseDeleteScope, chooseEditScope, isSeries } from './seriesScope';

export type TxKind = 'expense' | 'income' | 'card' | 'transfer';
export const TX_KIND_LABEL: Record<TxKind, string> = { expense: 'Despesa', income: 'Receita', card: 'Despesa no cartão', transfer: 'Transferência' };
export const TX_KIND_ICON: Record<TxKind, string> = { expense: 'arrow-up-right', income: 'arrow-down-left', card: 'credit-card', transfer: 'arrows-left-right' };

type Repeat = 'none' | 'installment' | 'recurring';
const REPEAT_LABEL: Record<Repeat, string> = { none: 'Não repete', installment: 'Parcelado', recurring: 'Recorrente' };
/** `frequency:interval` como a regra grava (recurringRepository.getFrequencyLabel) */
const FREQUENCIES: [string, string][] = [
  ['weekly:1', 'Semanal'], ['weekly:2', 'Quinzenal'], ['monthly:1', 'Mensal'], ['monthly:2', 'Bimestral'],
  ['monthly:3', 'Trimestral'], ['monthly:6', 'Semestral'], ['annual:1', 'Anual'],
];

interface Props {
  model: Model;
  tx?: TransactionRow;
  /** tipo já escolhido no menu do Lançar */
  initialKind?: TxKind;
  /** vindo de "Novo lançamento em <conta>" na página de Contas */
  initialAccountId?: string;
  initialDate?: string;
  /** vindo de "Novo lançamento em <categoria>" na página de Categorias */
  initialCategoryId?: string;
  initialSubcategoryId?: string;
  onClose: () => void;
  onSaved: () => Promise<void>;
}

function kindOf(tx: TransactionRow | undefined, initial: TxKind | undefined): TxKind {
  if (!tx) return initial ?? 'expense';
  if (tx.type === 'transfer') return 'transfer';
  if (tx.creditCardId) return 'card';
  return tx.type === 'income' ? 'income' : 'expense';
}

function CategoryChip({ color, icon }: { color: string; icon: string }) {
  return (
    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md" style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}>
      <CategoryIcon name={icon} size={13} />
    </span>
  );
}

const segment = (on: boolean, tone: 'neutral' | 'pos' | 'neg' = 'neutral') =>
  `flex items-center justify-center gap-1.5 rounded-xl border px-2 py-2 text-[13px] font-semibold disabled:opacity-50 ${
    on ? (tone === 'pos' ? 'border-pos bg-pos-pale text-pos' : tone === 'neg' ? 'border-neg bg-neg-pale text-neg' : 'border-teal bg-teal-pale text-teal-dark') : 'border-rule text-mute hover:border-teal-light'
  }`;

export function TransactionForm({ model, tx, initialKind, initialAccountId, initialDate, initialCategoryId, initialSubcategoryId, onClose, onSaved }: Props) {
  const editing = !!tx;
  const series = !!(tx?.installmentNumber || tx?.recurringId);
  // Ocorrência só projetada (recorrência ainda não materializada) não existe no banco: edição pelo app.
  const projected = !!tx?.isProjected;
  const [kind, setKind] = useState<TxKind>(() => kindOf(tx, initialKind));
  const isCard = kind === 'card';
  const isTransfer = kind === 'transfer';
  const type = kind === 'income' ? 'income' : isTransfer ? 'transfer' : 'expense';

  const [amount, setAmount] = useState(tx ? String(Math.abs(tx.amount)).replace('.', ',') : '');
  const [description, setDescription] = useState(tx?.description ?? '');
  const [date, setDate] = useState(tx?.date?.slice(0, 10) ?? initialDate ?? today());
  const [origin, setOrigin] = useState<string>(tx?.creditCardId ?? tx?.accountId ?? initialAccountId ?? '');
  const [dest, setDest] = useState<string>(tx?.transferDestinationAccountId ?? '');
  const [categoryId, setCategoryId] = useState(tx?.categoryId ?? initialCategoryId ?? '');
  const [subcategoryId, setSubcategoryId] = useState(tx?.subcategoryId ?? initialSubcategoryId ?? '');
  const [confirmed, setConfirmed] = useState(tx ? !!tx.isConfirmed : true);
  const [notes, setNotes] = useState('');
  const [repeat, setRepeat] = useState<Repeat>('none');
  const [count, setCount] = useState('2');
  /** "Parcela atual" (como no app): compra já em andamento entra dali em diante, sem retroativo. */
  const [current, setCurrent] = useState('1');
  const [valueIs, setValueIs] = useState<'total' | 'each'>('total');
  const [freq, setFreq] = useState('monthly:1');
  const [endMode, setEndMode] = useState<'never' | 'after' | 'on'>('never');
  const [endCount, setEndCount] = useState('12');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // O modelo das telas não carrega observações; ao editar, busca a linha inteira no banco.
  useEffect(() => {
    if (!tx) return;
    void getTransactionById(tx.id).then((full) => { if (full?.notes) setNotes(full.notes); });
  }, [tx]);

  const accountOptions = useMemo<PickerOption[]>(() => model.accounts.map((a) => ({
    id: a.id, label: a.name, hint: ACCOUNT_TYPE_LABEL[a.type] ?? '',
    icon: <InstitutionLogo icon={a.institutionIcon} institution={a.institution} name={a.name} color={a.color} size={24} />,
  })), [model]);
  const cardOptions = useMemo<PickerOption[]>(() => model.cards.map((c) => ({
    id: c.id, label: c.name, hint: c.lastFourDigits ? `•••• ${c.lastFourDigits}` : '',
    icon: <InstitutionLogo icon={c.institutionIcon} institution={c.institution} name={c.name} color={c.color} size={24} />,
  })), [model]);
  const originOptions = isCard ? cardOptions : accountOptions;
  // Trocar o tipo troca a lista: se a origem atual não serve mais, cai na primeira.
  useEffect(() => {
    if (origin && originOptions.some((o) => o.id === origin)) return;
    setOrigin(originOptions[0]?.id ?? '');
  }, [originOptions, origin]);
  const destOptions = useMemo(() => accountOptions.filter((o) => o.id !== origin), [accountOptions, origin]);

  const categoryType = type === 'income' ? 'income' : 'expense';
  const categoryOptions = useMemo<PickerOption[]>(() => [...model.categories.values()]
    .filter((c) => c.type === categoryType && !c.deletedAt && !c.isArchived)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((c) => ({ id: c.id, label: c.name, icon: <CategoryChip color={c.color} icon={c.icon} /> })), [model, categoryType]);
  const subcategoryOptions = useMemo<PickerOption[]>(() => [...model.subcategories.values()]
    .filter((s) => s.categoryId === categoryId && !s.deletedAt)
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((s) => ({ id: s.id, label: s.name })), [model, categoryId]);

  function changeKind(k: TxKind) {
    setKind(k);
    if ((k === 'income') !== (kind === 'income')) { setCategoryId(''); setSubcategoryId(''); }
    if (k === 'transfer' && repeat === 'installment') setRepeat('none');
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const value = parseAmount(amount);
    if (!(value > 0)) { setError('Informe um valor maior que zero.'); return; }
    if (!description.trim() && !isTransfer) { setError('Dê uma descrição ao lançamento.'); return; }
    if (!origin) { setError(isCard ? 'Escolha o cartão.' : isTransfer ? 'Escolha a conta de origem.' : 'Escolha a conta.'); return; }
    if (isTransfer && !dest) { setError('Escolha a conta de destino.'); return; }
    if (isTransfer && dest === origin) { setError('Origem e destino precisam ser contas diferentes.'); return; }
    const n = Math.max(2, Math.min(72, Math.round(Number(count) || 0)));
    if (!editing && repeat === 'installment' && !(Number(count) >= 2)) { setError('Parcelado precisa de pelo menos 2 parcelas.'); return; }
    const cur = parseCurrentInstallment(current, n);
    if (!editing && repeat === 'installment' && cur === null) { setError(`A parcela atual vai de 1 a ${n}.`); return; }
    if (!editing && repeat === 'recurring' && endMode === 'after' && !(Number(endCount) >= 1)) { setError('Informe quantas vezes a recorrência repete.'); return; }
    if (!editing && repeat === 'recurring' && endMode === 'on' && !endDate) { setError('Informe até quando a recorrência vale.'); return; }

    // Compra no cartão nasce efetivada (quem liquida é a fatura), salvo se a data for futura — como no app.
    const confirmedState = isCard ? date <= today() : confirmed;
    const desc = description.trim() || (isTransfer ? 'Transferência' : '');
    const base = {
      type,
      amount: value,
      description: desc,
      date,
      notes: notes.trim() || undefined,
      accountId: isCard ? undefined : origin,
      creditCardId: isCard ? origin : undefined,
      categoryId: isTransfer ? undefined : categoryId || undefined,
      subcategoryId: isTransfer ? undefined : subcategoryId || undefined,
      transferDestinationAccountId: isTransfer ? dest : undefined,
      isConfirmed: confirmedState,
    };
    setBusy(true); setError(null);
    try {
      if (tx) {
        if (isSeries(tx)) {
          // Série: o mesmo sheet de escopo do app (apenas esta / esta e futuras / todas, com as regras de fatura paga).
          setBusy(false);
          const mode = await chooseEditScope(tx, { date, amount: value, accountId: isCard ? null : origin, creditCardId: isCard ? origin : null, isConfirmed: confirmedState });
          if (!mode) return;
          setBusy(true);
          const ok = await applySeriesUpdate(tx, { ...base, notes: notes.trim() }, mode);
          if (!ok) throw new Error('Essa ocorrência não existe mais na série.');
        } else {
          await updateTransaction(tx.id, { ...base, notes: notes.trim() }, 'single');
        }
      } else if (repeat === 'installment') {
        const total = valueIs === 'total' ? value : value * n;
        if (isCard) {
          await createInstallment({
            description: desc || 'Parcelamento', totalAmount: total, installmentCount: n, startInstallmentNumber: cur ?? 1,
            creditCardId: origin, categoryId: base.categoryId, subcategoryId: base.subcategoryId, date, notes: base.notes,
          });
        } else {
          // Parcelado em conta: série manual com os campos de parcela, como o form do app faz,
          // da parcela atual em diante (a Data é a dela; as seguintes, um mês depois cada).
          const installmentId = generateId();
          const first = cur ?? 1;
          for (const p of planInstallments(total, n, first, date)) {
            await createTransaction({
              ...base,
              amount: p.amount,
              date: p.date,
              description: `${desc} (${p.number}/${n})`,
              isConfirmed: p.number === first ? confirmedState : false,
              installmentId, installmentNumber: p.number, installmentTotal: n,
            });
          }
        }
      } else if (repeat === 'recurring') {
        const [frequency, interval] = freq.split(':');
        await createWithInitialOccurrence({
          type, amount: value, description: desc, frequency, interval: Number(interval) || 1,
          count: endMode === 'after' ? Math.round(Number(endCount)) : null,
          startDate: date, endDate: endMode === 'on' ? endDate : null,
          accountId: base.accountId, creditCardId: base.creditCardId,
          categoryId: base.categoryId, subcategoryId: base.subcategoryId,
          transferDestinationAccountId: base.transferDestinationAccountId,
          date, notes: base.notes, isConfirmed: confirmedState,
        });
      } else if (isTransfer) {
        await createTransfer({ amount: value, description: desc, date, sourceAccountId: origin, destinationAccountId: dest, notes: base.notes, isConfirmed: confirmed });
      } else {
        await createTransaction(base);
      }
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  async function remove() {
    if (!tx) return;
    if (isSeries(tx)) {
      const mode = await chooseDeleteScope(tx);
      if (!mode) return;
      setBusy(true); setError(null);
      try { await applySeriesDelete(tx, mode); await onSaved(); } catch (err) { setError(err instanceof Error ? err.message : String(err)); setBusy(false); }
      return;
    }
    if (!(await askConfirm({ title: 'Excluir este lançamento?', text: 'Ele vai pra lixeira do app.', confirmLabel: 'Excluir', danger: true }))) return;
    setBusy(true); setError(null);
    try {
      await deleteTransaction(tx.id, 'single');
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const confirmLabel = isTransfer ? 'Já transferi' : kind === 'income' ? 'Já recebi' : 'Já paguei';

  // Prévia do parcelado a partir da parcela atual (o texto do app: "A partir da 3/10: 8x de …").
  const countNow = Math.max(2, Math.min(72, Math.round(Number(count) || 0)));
  const currentNow = parseCurrentInstallment(current, countNow);
  const amountNow = parseAmount(amount);
  const fromPreview = repeat === 'installment' && currentNow !== null && currentNow > 1
    ? { cur: currentNow, ...(amountNow > 0 ? installmentPreview(valueIs === 'total' ? amountNow : amountNow * countNow, countNow, currentNow) : { times: countNow - currentNow + 1, amount: 0, sum: 0 }) }
    : null;

  return (
    <Modal title={editing ? `Editar ${TX_KIND_LABEL[kind].toLowerCase()}` : TX_KIND_LABEL[kind]} onClose={onClose} testId="tx-form">
      <form onSubmit={submit} className="mt-4 space-y-4">
        {series && <p className="rounded-xl bg-teal-pale px-3 py-2 text-[13px] text-teal-dark">{projected ? 'Esta cobrança ainda é uma previsão da recorrência.' : tx?.installmentNumber ? 'Esta é uma parcela de uma compra parcelada.' : 'Este lançamento faz parte de uma recorrência.'} Ao salvar ou excluir, você escolhe se vale só pra esta, pra esta e as futuras ou pra todas, como no app.</p>}

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4" role="radiogroup" aria-label="Tipo">
          {(['expense', 'income', 'card', 'transfer'] as TxKind[]).map((k) => (
            <button key={k} type="button" role="radio" aria-checked={kind === k} onClick={() => changeKind(k)} disabled={editing}
              className={segment(kind === k, k === 'income' ? 'pos' : k === 'expense' ? 'neg' : 'neutral')} data-testid={`kind-${k}`}>
              <CategoryIcon name={TX_KIND_ICON[k]} size={14} />{k === 'card' ? 'Cartão' : TX_KIND_LABEL[k]}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field text={!editing && repeat === 'installment' && valueIs === 'each' ? 'Valor da parcela' : 'Valor'}>
            <input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" className={field} data-testid="amount" />
          </Field>
          <Field text="Data"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={field} data-testid="date" /></Field>
        </div>
        <Field text="Descrição"><input value={description} onChange={(e) => setDescription(e.target.value)} placeholder={isTransfer ? 'Ex.: Guardar pra viagem' : 'Ex.: Mercado'} className={field} data-testid="description" /></Field>

        <div className={isTransfer ? 'grid grid-cols-2 gap-3' : ''}>
          <FieldBox text={isCard ? 'Cartão' : isTransfer ? 'De' : 'Conta'}>
            <Picker value={origin} options={originOptions} onChange={setOrigin} disabled={editing && series} testId="origin"
              placeholder={isCard ? 'Escolha o cartão…' : 'Escolha a conta…'} />
          </FieldBox>
          {isTransfer && (
            <FieldBox text="Para">
              <Picker value={dest} options={destOptions} onChange={setDest} disabled={editing && series} testId="dest" placeholder="Escolha a conta…" />
            </FieldBox>
          )}
        </div>
        {isCard && !editing && <p className="-mt-2 text-[12px] text-mute">Entra na fatura pela data da compra; quem quita é o pagamento da fatura.</p>}

        {!isTransfer && (
          <div className="grid grid-cols-2 gap-3">
            <FieldBox text="Categoria">
              <Picker value={categoryId} options={categoryOptions} onChange={(id) => { setCategoryId(id); setSubcategoryId(''); }} emptyLabel="Sem categoria" testId="category" />
            </FieldBox>
            <FieldBox text="Subcategoria">
              <Picker value={subcategoryId} options={subcategoryOptions} onChange={setSubcategoryId} emptyLabel="—" disabled={subcategoryOptions.length === 0} testId="subcategory" />
            </FieldBox>
          </div>
        )}

        {!editing && (
          <FieldBox text="Repetição">
            <div className="mt-1 grid grid-cols-3 gap-2" role="radiogroup" aria-label="Repetição">
              {(['none', 'installment', 'recurring'] as Repeat[]).map((r) => (
                <button key={r} type="button" role="radio" aria-checked={repeat === r} onClick={() => setRepeat(r)} disabled={r === 'installment' && isTransfer}
                  className={segment(repeat === r)} data-testid={`repeat-${r}`}>
                  {r === 'recurring' && <CategoryIcon name="repeat" size={13} />}{REPEAT_LABEL[r]}
                </button>
              ))}
            </div>
            {repeat === 'installment' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field text="Parcelas"><input type="number" min={2} max={72} value={count} onChange={(e) => setCount(e.target.value)} className={field} data-testid="installments" /></Field>
                <Field text="Parcela atual"><input type="number" min={1} max={countNow} value={current} onChange={(e) => setCurrent(e.target.value)} className={field} data-testid="installment-current" /></Field>
                <div className="col-span-2">
                  <FieldBox text="O valor acima é">
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      <button type="button" onClick={() => setValueIs('total')} className={segment(valueIs === 'total')}>o total da compra</button>
                      <button type="button" onClick={() => setValueIs('each')} className={segment(valueIs === 'each')}>por parcela</button>
                    </div>
                  </FieldBox>
                </div>
                {fromPreview && (
                  <p className="col-span-2 -mt-1 text-[12px] text-mute" data-testid="installment-from">
                    Cria da parcela {fromPreview.cur}/{countNow} em diante, sem retroativo
                    {fromPreview.amount > 0 ? `: ${fromPreview.times}x de ${brl(fromPreview.amount)} (${brl(fromPreview.sum)})` : ''}.
                    {' '}Use na Data o dia da parcela {fromPreview.cur}/{countNow}.
                  </p>
                )}
                {isCard && <p className="col-span-2 -mt-1 text-[12px] text-mute">As parcelas caem nas faturas seguintes, uma por mês.</p>}
              </div>
            )}
            {repeat === 'recurring' && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Field text="Frequência">
                  <select value={freq} onChange={(e) => setFreq(e.target.value)} className={field} data-testid="frequency">
                    {FREQUENCIES.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </Field>
                <Field text="Termina">
                  <select value={endMode} onChange={(e) => setEndMode(e.target.value as typeof endMode)} className={field} data-testid="end-mode">
                    <option value="never">Nunca</option>
                    <option value="after">Depois de N vezes</option>
                    <option value="on">Numa data</option>
                  </select>
                </Field>
                {endMode === 'after' && <Field text="Vezes"><input type="number" min={1} max={600} value={endCount} onChange={(e) => setEndCount(e.target.value)} className={field} /></Field>}
                {endMode === 'on' && <Field text="Até"><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={field} /></Field>}
              </div>
            )}
          </FieldBox>
        )}

        {!isCard && (
          <label className="flex items-center gap-2 text-[14px] text-ink">
            <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="confirmed" />
            {confirmLabel} <span className="text-mute">(desmarcado = previsto)</span>
          </label>
        )}
        <Field text="Observações"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={field} /></Field>
        {error && <p className="rounded-xl bg-neg-pale px-3 py-2 text-[13px] text-neg" data-testid="form-error">{error}</p>}
        <Actions busy={busy} onCancel={onClose} onDelete={editing ? remove : undefined} />
      </form>
    </Modal>
  );
}
